import { FieldSet, RecordData, Records } from "airtable"
import Link from "next/link"
import ReactMarkdown from "react-markdown"
import tw, { styled } from "twin.macro"
import { CardRecommendedProps, MediaKicker, RecommendedImage } from "../recommended"

export enum ResourceType {
  Archival = "Archival",
  Indices = "Indices",
  Recommended = "Recommended",
}

const Arrow = () => {
  return (
    <svg width="15" height="56" viewBox="0 0 20 60" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 2L17 28.4727L2 58" stroke="#E6E3E3" strokeWidth="4" strokeLinecap="round" />
    </svg>
  )
}

interface StyledCardProps {
  cardtype?: string
}

const StyledCard = styled("div")(({ cardtype }: StyledCardProps) => [
  tw`bg-rpd-blue px-6 py-5 flex justify-between items-center space-x-8`,
  cardtype == ResourceType.Indices && tw`bg-rpd-red`,
])

interface CardProps {
  id: string
  fields: FieldSet
}

const Card = (props: CardProps) => {
  const { fields } = props
  const { type, title, id } = fields

  if (type == ResourceType.Recommended || !title || !type) {
    return <></>
  }

  return (
    <Link href={`./resources/${id}/`} passHref tw="hover:(text-white)" title={`View ${title}`}>
      <StyledCard cardtype={type ? type.toString() : ""}>
        <p tw="text-white text-[30px] leading-[36px] h-full min-h-[110px]" css="letter-spacing: -0.04em;">
          {title ? title.toString() : ""}
        </p>
        <div tw="h-full">
          <Arrow />
        </div>
      </StyledCard>
    </Link>
  )
}

const LongArrow = (
  <svg width="32" height="8" viewBox="0 0 32 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M31.3536 4.35355C31.5488 4.15829 31.5488 3.84171 31.3536 3.64645L28.1716 0.464466C27.9763 0.269204 27.6597 0.269204 27.4645 0.464466C27.2692 0.659728 27.2692 0.976311 27.4645 1.17157L30.2929 4L27.4645 6.82843C27.2692 7.02369 27.2692 7.34027 27.4645 7.53553C27.6597 7.7308 27.9763 7.7308 28.1716 7.53553L31.3536 4.35355ZM0 4.5H31V3.5H0V4.5Z"
      fill="#4D6BA1"
    />
  </svg>
)

interface SectionProps {
  records: Records<FieldSet>
  heading: string
  type: ResourceType
}

const CardRecommended = (props: CardRecommendedProps) => {
  const { body, image_url, caption, type, link, media } = props

  if (link && type && body && media) {
    const url = link.toString()
    const captionText = caption ? caption.toString() : ""
    const pageType = type.toString()
    const bodyText = body.toString()
    const mediaText = media.toString()

    return (
      <div tw="w-60">
        <div tw="space-y-4 tablet:(space-y-8)">
          <RecommendedImage image_url={image_url} caption={captionText} link={url} />
          <div>
            <h3 tw="uppercase font-sans text-sm text-rpd-blue font-bold">
              <Link href={url} target="_new" passHref>
                {pageType}
              </Link>
            </h3>
            <div tw="font-sans text-sm">
              <ReactMarkdown>{bodyText}</ReactMarkdown>
            </div>
            <MediaKicker media={mediaText} link={url} />
          </div>
        </div>
      </div>
    )
  }
  return <></>
}

const Section = ({ records, heading, type }: SectionProps) => {
  return (
    <section>
      <div tw="max-w-screen-desktop-lg desktop-lg:mx-auto px-4 relative">
        <div tw="py-2 shadow-lg bg-rpd-gray-2 bg-opacity-20">
          <div tw="px-4">
            <div tw="grid grid-cols-4 desktop:(grid-cols-12) gap-4 desktop:(gap-6 gap-y-0)">
              <div tw="col-span-4 tablet:(col-span-12)">
                <div tw="flex justify-between items-center space-x-4">
                  <h2 tw="py-2 text-xl text-rpd-gray-8 font-sans uppercase">{heading}</h2>
                  {type == ResourceType.Recommended && (
                    <Link
                      tw="uppercase text-xs font-sans flex items-center space-x-2 text-rpd-blue"
                      href="/resources/recommended"
                    >
                      <span tw="whitespace-nowrap">See all</span> <span>{LongArrow}</span>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div tw="p-4 py-2">
            <div tw="grid grid-flow-col overflow-auto">
              {type == ResourceType.Recommended ? (
                <div tw="grid grid-flow-col overflow-auto gap-x-6">
                  {records.map((item: any, i: number) => {
                    return <CardRecommended {...item.fields} key={i} />
                  })}
                </div>
              ) : (
                <div tw="grid grid-cols-4 tablet:(grid-cols-12) gap-4 desktop:(gap-6 gap-y-0)">
                  {records.map((item: RecordData<FieldSet>, i: number) => {
                    return (
                      <div tw="col-span-4 tablet:(col-span-6) desktop:(col-span-3)" key={i}>
                        <Card {...item} />
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

interface ResourcesProps {
  contentData: Records<FieldSet>
  recommededData?: Records<FieldSet>
}

const Resources = (props: ResourcesProps) => {
  const { contentData, recommededData } = props

  const archivalData: Records<FieldSet> = contentData.filter(
    (el: RecordData<FieldSet>) => el.fields.type === ResourceType.Archival,
  )
  const indices: Records<FieldSet> = contentData.filter((el: any) => el.fields.type === ResourceType.Indices)

  return (
    <>
      {recommededData && (
        <Section type={ResourceType.Recommended} records={recommededData} heading="Recommended viewing and reading" />
      )}
      <Section type={ResourceType.Archival} records={archivalData} heading="Archival collections" />
      <Section type={ResourceType.Indices} records={indices} heading="Indices" />
    </>
  )
}

export default Resources
